import { RebateOverview } from "@/gql/graphql";
import { InfoPair } from "@/components/info-pair/InfoPair";
import { formatThousands } from "@/lib/formatters/numbers";
import { formatAmountWithCurrency } from "../lib/rebateAmountUtils";
import { IndentValues } from "@/lib/utils/IndentValues";
import { Box, Column, Heading, Row, Txt } from "@stenajs-webui/core";
import { Tag } from "@stenajs-webui/elements";
import { Drawer, DrawerHeader } from "@stenajs-webui/modal";
import { cssColor } from "@stenajs-webui/theme";
import { formatPeriod } from "../lib/formatter";
import {
  getAchievedGoal,
  getActiveGoal,
  getCriteriaDescriptions,
  getCriteriaNames,
  getExpectedGoal,
} from "../lib/rebateOverviewUtils";
import { formatDate } from "@/lib/formatters/DateFormatter";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";

interface Props {
  selectedPaymentStatus?: RebateOverview;
  isOpen: boolean;
  onRequestClose: () => void;
}

export const HandleRebatesDrawer = ({
  selectedPaymentStatus,
  isOpen,
  onRequestClose,
}: Props) => {
  const sortedGoals = [...(selectedPaymentStatus?.goals ?? [])].sort(
    (a, b) => a.targetValue - b.targetValue
  );
  const activeGoal = getActiveGoal(selectedPaymentStatus);
  const achievedGoal = getAchievedGoal(selectedPaymentStatus);
  const expectedGoal = getExpectedGoal(selectedPaymentStatus);
  const achievedGoalId = achievedGoal?.id;
  const expectedGoalId = expectedGoal?.id;
  const achieved =
    selectedPaymentStatus !== undefined
      ? formatThousands(selectedPaymentStatus.achieved)
      : "-";
  const expected = expectedGoal
    ? formatThousands(expectedGoal.expectedVolume)
    : "-";
  const currencyCode = selectedPaymentStatus?.rebateAgreement?.currencyCode;
  const reserved = selectedPaymentStatus
    ? formatAmountWithCurrency(
        selectedPaymentStatus.reservedAmount,
        currencyCode
      )
    : "-";
  const paid = activeGoal?.amountPaid
    ? formatAmountWithCurrency(activeGoal.amountPaid, currencyCode)
    : "-";
  const period = selectedPaymentStatus
    ? formatPeriod(
        selectedPaymentStatus.period,
        selectedPaymentStatus.rebateTermCode,
        sortedGoals[0]?.validFrom
      )
    : "";
  return (
    <Drawer
      id="HandleRebatesDrawer"
      slideFrom="right"
      isOpen={isOpen}
      zIndex={9999}
      floating
      onRequestClose={onRequestClose}
    >
      <DrawerHeader
        onRequestClose={onRequestClose}
        header={`${selectedPaymentStatus?.rebateAgreement?.rebateAgreementNumber}, ${selectedPaymentStatus?.rebateAgreement?.name}`}
      />
      <Box
        indent={IndentValues.SIXTEEN}
        gap={IndentValues.SIXTEEN}
        data-testid={testIdConstants.handleRebatesSummaryDrawer}
      >
        <Column>
          <InfoPair
            leftContent={"Agreement No"}
            rightContent={
              selectedPaymentStatus?.rebateAgreement?.rebateAgreementNumber ??
              "-"
            }
          />
          <InfoPair
            leftContent={"Agreement"}
            rightContent={selectedPaymentStatus?.rebateAgreement?.name ?? "-"}
          />
          <InfoPair
            leftContent={"Criteria"}
            rightContent={getCriteriaNames(selectedPaymentStatus) || "-"}
          />
          <InfoPair
            leftContent={"Description"}
            rightContent={getCriteriaDescriptions(selectedPaymentStatus) || "-"}
          />
          <InfoPair leftContent={"Period"} rightContent={period || "-"} />
          <InfoPair leftContent={"Achieved"} rightContent={achieved} />
          <InfoPair leftContent={"Expected"} rightContent={expected} />
          <InfoPair
            leftContent={"Reserved"}
            rightContent={reserved}
            rightContentDataTestid={
              testIdConstants.handleRebatesSummaryDrawerReservedValue
            }
          />
          <InfoPair leftContent={"Paid"} rightContent={paid} />
          <InfoPair
            leftContent={"Paid date"}
            rightContent={
              activeGoal?.datePaid ? formatDate(activeGoal?.datePaid) : "-"
            }
          />
          <InfoPair
            leftContent={"Authorized by"}
            rightContent={expectedGoal?.authorizedBy ?? "-"}
          />
          <InfoPair
            leftContent={"Credit notes"}
            rightContent={
              selectedPaymentStatus?.creditNoteBookingNumbers.join(", ") ?? "-"
            }
          />
        </Column>

        <Column gap={2}>
          <Heading variant={"h5"}>Target & Rebate</Heading>
          <Box
            borderRadius={"var(--swui-border-radius-small)"}
            border={`1px solid ${cssColor("--lhds-color-ui-300")}`}
          >
            <Row background={cssColor("--lhds-color-ui-200")} indent={2}>
              <Box flex={1} spacing={1}>
                <Txt variant={"bold"}>Target</Txt>
              </Box>
              <Box flex={1} spacing={1}>
                <Txt variant={"bold"}>Rebate</Txt>
              </Box>
              <Box flex={1} spacing={1}>
                <Txt variant={"bold"}>Status</Txt>
              </Box>
            </Row>
            {sortedGoals.length === 0 ? (
              <Box indent={2} spacing={1}>
                <Txt>-</Txt>
              </Box>
            ) : (
              sortedGoals.map((goal) => {
                const isAchieved = goal.id === achievedGoalId;
                const isExpected = goal.id === expectedGoalId;
                return (
                  <Row
                    key={goal.id}
                    indent={2}
                    alignItems={"center"}
                    borderTop={`1px solid ${cssColor("--lhds-color-ui-200")}`}
                  >
                    <Box flex={1} spacing={1}>
                      <Txt>{formatThousands(goal.targetValue)}</Txt>
                    </Box>
                    <Box flex={1} spacing={1}>
                      <Txt whiteSpace={"nowrap"}>
                        {formatAmountWithCurrency(
                          goal.rebateRate,
                          currencyCode
                        )}
                      </Txt>
                    </Box>
                    <Box flex={1} spacing={1} minWidth={0}>
                      <Column gap={1} alignItems={"flex-start"}>
                        {isAchieved && (
                          <Tag label={"Achieved"} variant={"success"} />
                        )}
                        {isExpected && (
                          <Tag label={"Expected"} variant={"info"} />
                        )}
                      </Column>
                    </Box>
                  </Row>
                );
              })
            )}
          </Box>
        </Column>
      </Box>
    </Drawer>
  );
};
